import type { MenuDataItem } from '@ant-design/pro-components';

// Keep settings in ProLayout's route matching, but outside the business header.
export const businessHeaderMenu = <T extends { path?: string }>(
  items: T[] = [],
): T[] => items.filter((item) => item.path !== '/settings');

export const accessibleLayoutMenu = (items: MenuDataItem[]): MenuDataItem[] =>
  items
    .filter((item) => !item.unaccessible)
    .map((item) => ({
      ...item,
      children: item.children ? accessibleLayoutMenu(item.children) : undefined,
    }));

/**
 * 侧栏展示层：
 * 1. 平铺投后经营与退出管理相关页面，去除冗余的「投后管理」中间折叠层；
 * 2. 依次平铺展示：概览、投后项目、退出管理、经营跟踪、风险与事项、定期报告；
 * 3. 隐藏的详情/兼容路由仍留在 /portfolio 下，保证现有菜单匹配与高亮不回归。
 */
export const nestPostInvestmentMenu = (
  items: MenuDataItem[] = [],
): MenuDataItem[] =>
  items.map((item) => {
    if (item.path !== '/portfolio' || !item.children) return item;
    const byPath = new Map(item.children.map((child) => [child.path, child]));
    const overview = byPath.get('/portfolio/overview');
    const portfolioProject = byPath.get('/portfolio/portfolio');
    const exit = byPath.get('/portfolio/exit');
    const operation = byPath.get('/portfolio/operation');
    const risk = byPath.get('/portfolio/risk-management');
    const report = byPath.get('/portfolio/report');
    // 隐藏的详情/兼容路由仍留在 /portfolio 下，保证现有菜单匹配与高亮不回归。
    const hiddenChildren = item.children.filter((child) => child.hideInMenu);

    const orderedVisibleChildren = [
      overview,
      portfolioProject,
      exit,
      operation,
      risk,
      report,
    ].filter((child): child is MenuDataItem => Boolean(child));

    return {
      ...item,
      children: [...orderedVisibleChildren, ...hiddenChildren],
    };
  });

export const settingsSiderMenu = (
  pathname: string,
  items: MenuDataItem[] = [],
) =>
  pathname === '/settings' || pathname.startsWith('/settings/')
    ? (items
        .find((item) => item.path === '/settings')
        ?.children?.filter((item) => !item.hideInMenu) ?? [])
    : undefined;
